import React from "react";
import { parseCitations } from "./ai/parse-response";

/**
 * Renders text with markdown formatting (bold, italic) and citations
 */
function renderFormattedText(text: string, onCitationClick: (tab: string, appointmentId?: string) => void): React.ReactNode {
  // Ensure text is always a string
  const textString = typeof text === "string" ? text : typeof text === "object" && text !== null ? JSON.stringify(text) : String(text || "");
  if (!textString) return null;
  const parts: React.ReactNode[] = [];
  const textLength = textString.length;

  // Pattern to match markdown formatting: **bold**, __bold__, *italic*, _italic_
  // Also matches citations: [[ref:tab|label]] or [[ref:tab:id|label]]
  // Order matters: match **bold** before *italic* to avoid matching ** as two italics
  // Handles cases like **text*** by matching the bold and cleaning trailing asterisks
  const formatPattern = /(\*\*[^*]+\*\*+|__[^_]+__+|\*[^*\n]+\*|_[^_\n]+_|\[\[ref:[^\]]+\]\])/g;
  let match;
  const matches: Array<{
    index: number;
    length: number;
    type: "bold" | "italic" | "citation";
    content: string;
  }> = [];

  // First, collect all citations (handle multiple occurrences)
  const {
    citations
  } = parseCitations(textString);
  // Track which positions we've already matched to avoid duplicates
  const matchedPositions = new Set<number>();
  citations.forEach(citation => {
    // Find all occurrences of this citation in the text
    let searchIndex = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const index = textString.indexOf(citation.original, searchIndex);
      if (index === -1) break;

      // Only add if we haven't already matched this position
      if (!matchedPositions.has(index)) {
        matches.push({
          index,
          length: citation.original.length,
          type: "citation",
          content: citation.original
        });
        // Mark all positions in this citation as matched
        for (let i = index; i < index + citation.original.length; i++) {
          matchedPositions.add(i);
        }
      }
      searchIndex = index + 1;
    }
  });

  // Then, collect all markdown formatting (avoiding overlaps with citations)
  // Create a set of citation positions for quick overlap checking
  const citationPositions = new Set<number>();
  citations.forEach(citation => {
    let searchIndex = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const index = textString.indexOf(citation.original, searchIndex);
      if (index === -1) break;
      // Mark all positions covered by this citation
      for (let i = index; i < index + citation.original.length; i++) {
        citationPositions.add(i);
      }
      searchIndex = index + 1;
    }
  });
  while ((match = formatPattern.exec(textString)) !== null) {
    const isCitation = match[0].startsWith("[[ref:");
    if (!isCitation) {
      // Check if this markdown formatting overlaps with any citation
      let overlapsCitation = false;
      for (let i = match.index; i < match.index + match[0].length; i++) {
        if (citationPositions.has(i)) {
          overlapsCitation = true;
          break;
        }
      }
      if (!overlapsCitation) {
        const isBold = match[0].startsWith("**") || match[0].startsWith("__");
        matches.push({
          index: match.index,
          length: match[0].length,
          type: isBold ? "bold" : "italic",
          content: match[0]
        });
      }
    }
  }

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  // Build React nodes
  let lastIndex = 0;

  // Track processed citation positions to avoid duplicates
  const processedCitations = new Set<number>();
  for (let idx = 0; idx < matches.length; idx++) {
    const match = matches[idx];

    // Skip if this citation position was already processed
    if (match.type === "citation" && processedCitations.has(match.index)) {
      continue;
    }

    // Add text before the match
    if (match.index > lastIndex) {
      const textBefore = textString.substring(lastIndex, match.index);
      if (textBefore) {
        parts.push(<span key={`text-${idx}`}>{textBefore}</span>);
      }
    }
    if (match.type === "citation") {
      // Mark this citation as processed
      processedCitations.add(match.index);

      // Find the citation object that matches this position
      const citation = citations.find(c => c.original === match.content);
      if (citation) {
        // Render the citation label (this replaces the entire citation marker)
        parts.push(<span key={`citation-${idx}-${match.index}`} onClick={() => onCitationClick(citation!.tab, citation!.appointmentId)} className="cursor-pointer font-medium text-primary hover:underline">
            {citation.label}
          </span>);
      }
      // Always skip the entire citation marker length, even if citation not found
      lastIndex = match.index + match.length;
    } else if (match.type === "bold") {
      // Extract text from **text** or __text__
      // Handle cases like **text*** by removing all trailing asterisks/underscores
      const content = match.content.replace(/^\*\*+|\*+$/g, "").replace(/^__+|_+$/g, "");
      // Recursively render nested formatting (e.g., bold with italic inside)
      parts.push(<strong key={`bold-${idx}`}>
          {renderFormattedText(content, onCitationClick)}
        </strong>);
      lastIndex = match.index + match.length;
    } else if (match.type === "italic") {
      // Extract text from *text* or _text_ - remove the markers cleanly
      let content = match.content;
      // Remove asterisk markers (handle both single and ensure we don't remove from middle)
      if (content.startsWith("*") && content.endsWith("*") && !content.startsWith("**")) {
        content = content.slice(1, -1);
      }
      // Remove underscore markers (handle both single and ensure we don't remove from middle)
      else if (content.startsWith("_") && content.endsWith("_") && !content.startsWith("__")) {
        content = content.slice(1, -1);
      }
      // Recursively render nested formatting
      parts.push(<em key={`italic-${idx}`}>
          {renderFormattedText(content, onCitationClick)}
        </em>);
      lastIndex = match.index + match.length;
    }
  }

  // Add remaining text
  if (lastIndex < textLength) {
    const textAfter = textString.substring(lastIndex);
    if (textAfter) {
      parts.push(<span key="text-end">{textAfter}</span>);
    }
  }
  return parts.length > 0 ? <>{parts}</> : <span>{textString}</span>;
}

/**
 * Renders a text segment (non-list content) with citations and markdown formatting
 */
function renderTextSegment(text: string, onCitationClick: (tab: string, appointmentId?: string) => void): React.ReactNode {
  return renderFormattedText(text, onCitationClick);
}

/**
 * Renders a list item with citations
 */
function renderListItem(itemText: string, onCitationClick: (tab: string, appointmentId?: string) => void): React.ReactNode {
  // Remove the list marker (number or bullet) and trim
  const cleanText = itemText.replace(/^\d+\.\s*/, "").replace(/^[-*]\s*/, "").trim();
  return <li className="pl-1">{renderTextSegment(cleanText, onCitationClick)}</li>;
}

/**
 * Renders text with proper list formatting and citations
 * Detects numbered lists (1. 2. 3.) and bulleted lists (- or *)
 * and converts them to proper HTML lists
 */
export function renderTextWithCitations(text: string, onCitationClick: (tab: string, appointmentId?: string) => void): React.ReactNode {
  // Ensure text is always a string
  const textString = typeof text === "string" ? text : typeof text === "object" && text !== null ? JSON.stringify(text) : String(text || "");
  if (!textString) return null;

  // Split text into segments: regular text and list blocks
  const segments: Array<{
    type: "text" | "list";
    content: string;
    isNumbered: boolean;
  }> = [];

  // Pattern to match numbered lists (1. 2. 3.) or bulleted lists (- or *)
  const listPattern = /(?:^|\n)((?:\d+\.\s+[^\n]+(?:\n|$))+)|(?:^|\n)((?:[-*]\s+[^\n]+(?:\n|$))+)/gm;
  let lastIndex = 0;
  let match;
  while ((match = listPattern.exec(textString)) !== null) {
    // Add text before the list
    if (match.index > lastIndex) {
      const textBefore = textString.substring(lastIndex, match.index).trim();
      if (textBefore) {
        segments.push({
          type: "text",
          content: textBefore,
          isNumbered: false
        });
      }
    }

    // Add the list
    const listContent = match[1] || match[2]; // Numbered or bulleted
    const isNumbered = !!match[1];
    segments.push({
      type: "list",
      content: listContent.trim(),
      isNumbered
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last list
  if (lastIndex < textString.length) {
    const textAfter = textString.substring(lastIndex).trim();
    if (textAfter) {
      segments.push({
        type: "text",
        content: textAfter,
        isNumbered: false
      });
    }
  }

  // If no lists found, check for inline numbered lists (e.g., "Consider: 1. item 2. item 3. item")
  if (segments.length === 0) {
    // Pattern to detect inline numbered lists (at least 2 consecutive numbered items)
    const inlineListPattern = /(\d+\.\s+[^.!?\n]+(?:\s+\d+\.\s+[^.!?\n]+){1,})/g;
    const inlineMatches: Array<{
      start: number;
      end: number;
      content: string;
    }> = [];
    let inlineMatch;
    while ((inlineMatch = inlineListPattern.exec(textString)) !== null) {
      inlineMatches.push({
        start: inlineMatch.index,
        end: inlineMatch.index + inlineMatch[0].length,
        content: inlineMatch[0]
      });
    }

    // If inline lists found, split text around them
    if (inlineMatches.length > 0) {
      const newSegments: Array<{
        type: "text" | "list";
        content: string;
        isNumbered: boolean;
      }> = [];
      let lastIdx = 0;
      for (const inlineMatch of inlineMatches) {
        // Add text before the list
        if (inlineMatch.start > lastIdx) {
          const textBefore = textString.substring(lastIdx, inlineMatch.start).trim();
          if (textBefore) {
            newSegments.push({
              type: "text",
              content: textBefore,
              isNumbered: false
            });
          }
        }

        // Add the inline list
        newSegments.push({
          type: "list",
          content: inlineMatch.content,
          isNumbered: true
        });
        lastIdx = inlineMatch.end;
      }

      // Add remaining text after last list
      if (lastIdx < textString.length) {
        const textAfter = textString.substring(lastIdx).trim();
        if (textAfter) {
          newSegments.push({
            type: "text",
            content: textAfter,
            isNumbered: false
          });
        }
      }

      // Render the new segments
      const result: React.ReactNode[] = [];
      newSegments.forEach((segment, idx) => {
        if (segment.type === "text") {
          result.push(<React.Fragment key={`text-${idx}`}>
              {renderTextSegment(segment.content, onCitationClick)}
            </React.Fragment>);
        } else if (segment.type === "list") {
          // Split inline list by number pattern
          const items = segment.content.split(/(?=\d+\.\s+)/).filter(Boolean);
          result.push(<ol key={`list-${idx}`} className="my-2 ml-4 list-decimal space-y-1">
              {items.map((item, itemIdx) => <React.Fragment key={`item-${idx}-${itemIdx}`}>
                  {renderListItem(item, onCitationClick)}
                </React.Fragment>)}
            </ol>);
        }
      });
      return <>{result}</>;
    }

    // No lists found, render as plain text
    return renderTextSegment(textString, onCitationClick);
  }

  // Render segments
  const result: React.ReactNode[] = [];
  segments.forEach((segment, idx) => {
    if (segment.type === "text") {
      result.push(<React.Fragment key={`text-${idx}`}>
          {renderTextSegment(segment.content, onCitationClick)}
        </React.Fragment>);
    } else if (segment.type === "list") {
      // Split list into individual items
      // First try splitting by newlines, then by number pattern if needed
      let items = segment.content.split(/\n(?=\d+\.\s+|\s*[-*]\s+)/).filter(Boolean);

      // If we only got one item and it's a numbered list, try splitting by number pattern
      if (items.length === 1 && segment.isNumbered) {
        const splitByNumber = segment.content.split(/(?=\d+\.\s+)/).filter(Boolean);
        if (splitByNumber.length > 1) {
          items = splitByNumber;
        }
      }
      const ListTag = segment.isNumbered ? "ol" : "ul";
      const listStyle = segment.isNumbered ? "list-decimal" : "list-disc";
      result.push(<ListTag key={`list-${idx}`} className={`my-2 ml-4 ${listStyle} space-y-1`}>
          {items.map((item, itemIdx) => <React.Fragment key={`item-${idx}-${itemIdx}`}>
              {renderListItem(item, onCitationClick)}
            </React.Fragment>)}
        </ListTag>);
    }
  });
  return <>{result}</>;
}